<template>
    <Modal
        id="create-or-edit-offer-modal"
        ref="create-or-edit-offer-modal"
        v-model:open="isOpen"
        :title="$t(isEdit ? 'edit_offer' : 'create_offer')"
        width="3xl"
        @cancel="cancel"
    >
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <!-- LEFT COLUMN: Offer Details -->
            <div class="space-y-4">
                <h3 class="text-sm font-bold text-slate-700">
                    {{ $t('offer_details') }}
                </h3>

                <!-- Asset Selection -->
                <SelectField
                    v-model="modelValue.asset_id"
                    name="asset_title"
                    value="asset_uid"
                    label="asset_title"
                    :options="assets"
                    :required="true"
                    :on-search="loadSelectAssetOptions"
                />

                <div
                    v-if="asset"
                    class="rounded border border-slate-200 bg-slate-50 p-3 text-right"
                >
                    <span class="mr-2 text-xs text-slate-500">
                        {{ $t('sm_transaction_fee') }}:
                    </span>
                    <span class="text-base text-slate-800">
                        {{ asset.sm_transaction_fee }} %
                    </span>
                </div>

                <!-- Seller Selection -->
                <SelectField
                    v-model="modelValue.seller_id"
                    :options="sellerAccounts"
                    name="account_title"
                    value="account_uid"
                    label="seller"
                    :disabled="isSellerBuyerDisabled"
                    :required="true"
                    :on-search="loadSelectSellerAccountOptions"
                />
                <div
                    v-if="modelValue.seller_id && modelValue.asset_id"
                    class="rounded border border-slate-200 bg-slate-50 p-3 text-right"
                >
                    <span class="mr-2 text-xs text-slate-500">
                        {{ $t('available_rights') }}:
                    </span>
                    <span class="text-base text-slate-800">
                        {{ availableRights }}
                    </span>
                </div>

                <!-- Buyer Selection -->
                <SelectField
                    v-model="modelValue.buyer_id"
                    :options="buyerAccounts"
                    name="account_title"
                    value="account_uid"
                    label="buyer"
                    :disabled="isSellerBuyerDisabled"
                    :required="true"
                    :on-search="loadSelectBuyerAccountOptions"
                />

                <!-- Quantity & Price per Unit -->
                <div class="grid grid-cols-2 gap-4">
                    <NumberField
                        v-model="modelValue.quantity"
                        :label="$t('quantity')"
                        name="quantity"
                        :required="true"
                        :min="1"
                    />

                    <NumberField
                        v-model="modelValue.price_per_unit"
                        :label="$t('price_per_unit')"
                        name="price_per_unit"
                        :required="true"
                        :min="0"
                    />
                </div>

                <!-- Status Selection -->
                <SelectField
                    v-model="modelValue.status"
                    name="status"
                    label="status"
                    :options="statusOptions"
                    :required="true"
                />

                <!-- Contract Document File Upload -->
                <div class="space-y-1">
                    <label class="block text-xs font-semibold text-slate-600">
                        {{ $t('contract_document') }}
                    </label>
                    <FileUploadButton
                        title="contract_document"
                        accept=".pdf"
                        :file-name="contractFile.name"
                        :url="contractFile.url"
                        @submit="onContractUpload"
                        @delete="onContractDelete"
                    />
                </div>

                <!-- Total Price Display (Calculated) -->
                <div
                    class="rounded border border-slate-200 bg-slate-50 p-3 text-right"
                >
                    <span class="mr-2 text-xs text-slate-500">
                        {{ $t('total_amount') }}:
                    </span>
                    <span class="text-base font-bold text-slate-800">
                        {{ calculatedTotal }} €
                    </span>
                </div>
            </div>

            <!-- RIGHT COLUMN: Transaction Details -->
            <div
                class="border-t border-slate-200 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0"
            >
                <div class="mb-4 flex items-center justify-between">
                    <h3 class="text-sm font-bold text-slate-700">
                        {{ $t('transaction') }}
                    </h3>
                    <button
                        v-if="isEdit && !hasTransaction && !enableTransaction"
                        type="button"
                        class="btn btn-sm bg-slate-800 text-xs text-white hover:bg-slate-700"
                        @click="enableTransaction = true"
                    >
                        + {{ $t('create_transaction') }}
                    </button>
                </div>

                <!-- Transaction Form Container -->
                <div
                    v-if="isEdit && (hasTransaction || enableTransaction)"
                    class="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
                >
                    <SelectField
                        v-model="transactionData.status"
                        name="transaction_status"
                        label="transaction_status"
                        :options="transactionStatusOptions"
                        :required="true"
                    />

                    <!-- Read-only Payment Reference -->
                    <div class="space-y-1">
                        <label
                            class="block text-xs font-semibold text-slate-600"
                        >
                            {{ $t('payment_reference') }}
                        </label>
                        <input
                            v-model="transactionData.payment_reference"
                            type="text"
                            readonly
                            class="form-input w-full cursor-not-allowed rounded-md border-slate-200 bg-slate-100 text-sm text-slate-600 focus:border-slate-200 focus:ring-0"
                            :placeholder="
                                transactionData.payment_reference ||
                                'BRH-PENDING'
                            "
                        />
                    </div>

                    <!-- Transaction Documents -->
                    <div class="space-y-3 pt-2">
                        <!-- Buyer Signed Contract -->
                        <div class="space-y-1">
                            <label
                                class="block text-xs font-semibold text-slate-600"
                            >
                                {{ $t('buyer_signed_contract') }}
                            </label>
                            <FileUploadButton
                                title="buyer_signed_contract"
                                accept=".pdf"
                                :file-name="buyerSignedFile.name"
                                :url="buyerSignedFile.url"
                                @submit="
                                    (res, name) =>
                                        onTxFileUpload(
                                            'buyer_signed',
                                            res,
                                            name
                                        )
                                "
                                @delete="() => onTxFileDelete('buyer_signed')"
                            />
                        </div>

                        <!-- Seller Signed Contract -->
                        <div class="space-y-1">
                            <label
                                class="block text-xs font-semibold text-slate-600"
                            >
                                {{ $t('seller_signed_contract') }}
                            </label>
                            <FileUploadButton
                                title="seller_signed_contract"
                                accept=".pdf"
                                :file-name="sellerSignedFile.name"
                                :url="sellerSignedFile.url"
                                @submit="
                                    (res, name) =>
                                        onTxFileUpload(
                                            'seller_signed',
                                            res,
                                            name
                                        )
                                "
                                @delete="() => onTxFileDelete('seller_signed')"
                            />
                        </div>
                    </div>
                </div>

                <!-- Empty state if editing and no transaction created yet -->
                <div
                    v-else-if="isEdit"
                    class="rounded-lg border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500"
                >
                    {{ $t('no_transaction_associated') }}
                </div>

                <!-- Info state for create mode -->
                <div
                    v-else
                    class="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400"
                >
                    {{ $t('transaction_will_be_created_on_save') }}
                </div>
            </div>
        </div>

        <template #actions>
            <button
                type="button"
                class="btn bg-primary-accent-500 hover:bg-primary-accent-600 text-white"
                :disabled="!isValid"
                @click="submit"
            >
                {{ $t(isEdit ? 'save' : 'create') }}
            </button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { Modal, NumberField, SelectField } from '@src/components'
import FileUploadButton from '@src/partials/actions/FileUploadButton.vue'
import { Account, Asset, Offer } from '@src/types'
import { UploadFileResponse } from '@src/utils/api/s3'
import { getAccounts } from '@src/utils/api/accounts'
import {
    getOffer,
    getPreEmptiveRights,
    getSmAssets,
} from '@src/utils/api/secondaryMarket'
import { useI18n } from 'vue-i18n'
import {
    OfferStatusType,
    SmAssetType,
    TransactionStatus,
} from '@src/enums/SecondaryMarket'
import { getAsset, getAssets } from '@utils/api/assets'

const props = withDefaults(
    defineProps<{
        open: boolean
        offer?: Offer | Record<string, any> | null
        platformName?: string | null
    }>(),
    {
        offer: null,
        platformName: null,
    }
)

const emit = defineEmits(['update:open', 'cancel', 'submit'])

const i18n = useI18n()

const sellerAccounts = ref<{ label: string; value: string }[]>([])
const buyerAccounts = ref<{ label: string; value: string }[]>([])
const assets = ref<{ label: string; value: string }[]>([])
const asset = ref<Asset | null>(null)
const availableRights = ref<number | null>(null)

const enableTransaction = ref(false)
const hasTransaction = ref(false)
const offerDetails = ref<any>(null)

const isOpen = computed({
    get: () => props.open,
    set: (val) => emit('update:open', val),
})

const isEdit = computed(() => !!props.offer?.id)

const isSellerBuyerDisabled = computed(
    () => isEdit.value && modelValue.value.status !== OfferStatusType.PENDING
)

const modelValue = ref({
    id: null as string | null,
    asset_id: null as string | null,
    seller_id: null as string | null,
    buyer_id: null as string | null,
    type: SmAssetType.PRE_EMPTIVE,
    quantity: 1,
    price_per_unit: 0,
    total_price: 0,
    status: OfferStatusType.ACCEPTED,
})

const contractFile = reactive({
    name: null as string | null,
    upload_key: null as string | null,
    url: null as string | null,
})

const transactionData = reactive({
    id: null as string | null,
    status: TransactionStatus.PENDING_PAYMENT,
    payment_reference: null as string | null,
})

const buyerSignedFile = reactive({
    name: null as string | null,
    upload_key: null as string | null,
    url: null as string | null,
})
const sellerSignedFile = reactive({
    name: null as string | null,
    upload_key: null as string | null,
    url: null as string | null,
})

const statusOptions = computed(() => [
    { label: i18n.t('offer_status_pending'), value: OfferStatusType.PENDING },
    { label: i18n.t('offer_status_accepted'), value: OfferStatusType.ACCEPTED },
    { label: i18n.t('offer_status_rejected'), value: OfferStatusType.REJECTED },
    { label: i18n.t('offer_status_expired'), value: OfferStatusType.EXPIRED },
])

const transactionStatusOptions = computed(() =>
    Object.values(TransactionStatus).map((status) => ({
        label: i18n.t(`transaction_status_${status}`),
        value: status,
    }))
)

const calculatedTotal = computed(() => {
    const qty = Number(modelValue.value.quantity) || 0
    const price = Number(modelValue.value.price_per_unit) || 0
    const baseTotal = qty * price

    const feePercentage = asset.value?.sm_transaction_fee
        ? Number(asset.value.sm_transaction_fee)
        : 0

    const grandTotal = baseTotal * (1 + feePercentage / 100)

    return grandTotal.toFixed(2)
})

const isValid = computed(() => {
    return (
        !!modelValue.value.asset_id &&
        !!modelValue.value.seller_id &&
        !!modelValue.value.buyer_id &&
        modelValue.value.quantity > 0 &&
        modelValue.value.price_per_unit >= 0 &&
        !!modelValue.value.status
    )
})

watch(
    () => props.offer,
    async (newVal) => {
        if (newVal && newVal.id) {
            try {
                // Fetch full details
                const response = await getOffer(newVal.id)

                const apiData =
                    response?.data?.data || response?.data || response
                offerDetails.value = apiData

                const offerData = apiData?.offer || newVal
                const txData = apiData?.transaction || null

                // Populate Seller. Only add the fallback entry if the
                // account search (loaded concurrently in onMounted) hasn't
                // already provided a better-labelled one for this id —
                // otherwise whichever of the two async calls resolves last
                // would randomly clobber the other's result.
                if (offerData.seller_id) {
                    const exists = sellerAccounts.value.some(
                        (a) => a.value === offerData.seller_id
                    )
                    if (!exists) {
                        sellerAccounts.value = [
                            {
                                label: `${offerData.seller_title} (${offerData.seller_email})`,
                                value: offerData.seller_id,
                            },
                            ...sellerAccounts.value,
                        ]
                    }
                }
                // Populate Buyer (same rationale as seller above).
                if (offerData.buyer_id) {
                    const exists = buyerAccounts.value.some(
                        (a) => a.value === offerData.buyer_id
                    )
                    if (!exists) {
                        buyerAccounts.value = [
                            {
                                label: `${offerData.buyer_title} (${offerData.buyer_email})`,
                                value: offerData.buyer_id,
                            },
                            ...buyerAccounts.value,
                        ]
                    }
                }

                // Populate Asset
                const assetId =
                    offerData.asset_id || offerData.asset_uid || null
                if (assetId) {
                    const assetTitle =
                        offerData.asset_title || offerData.asset_name || assetId
                    const exists = assets.value.some((a) => a.value === assetId)
                    if (!exists) {
                        assets.value = [
                            { label: assetTitle, value: assetId },
                            ...assets.value,
                        ]
                    }
                }

                modelValue.value = {
                    id: offerData.id || null,
                    asset_id: assetId,
                    seller_id: offerData.seller_id || null,
                    buyer_id: offerData.buyer_id || null,
                    type: SmAssetType.PRE_EMPTIVE,
                    quantity: offerData.quantity || 1,
                    price_per_unit: offerData.price_per_unit || 0,
                    total_price: offerData.total_price || 0,
                    status: offerData.status || OfferStatusType.ACCEPTED,
                }

                // Contract file metadata
                contractFile.name =
                    offerData.contract_name ||
                    offerData.contract_document_name ||
                    (offerData.contract_url ? 'Contract.pdf' : null)
                contractFile.upload_key =
                    offerData.contract_upload_key ||
                    offerData.upload_key ||
                    null
                contractFile.url = offerData.contract_url || null

                // Transaction resolution
                if (txData && txData.id) {
                    hasTransaction.value = true
                    enableTransaction.value = true

                    transactionData.id = txData.id
                    transactionData.status =
                        txData.status || TransactionStatus.PENDING_PAYMENT
                    transactionData.payment_reference =
                        txData.payment_reference || null

                    buyerSignedFile.url =
                        txData.buyer_signed_contract_url || null
                    buyerSignedFile.name = txData.buyer_signed_contract_url
                        ? 'buyer_signed.pdf'
                        : null

                    sellerSignedFile.url =
                        txData.seller_signed_contract_url || null
                    sellerSignedFile.name = txData.seller_signed_contract_url
                        ? 'seller_signed.pdf'
                        : null
                } else {
                    hasTransaction.value = false
                    enableTransaction.value = false
                    resetTxForm()
                }

                if (modelValue.value.asset_id) {
                    await loadSelectedAsset()
                }
                if (modelValue.value.seller_id && modelValue.value.asset_id) {
                    await getQuantityForAccount()
                }
            } catch (error) {
                console.error('Failed to load full offer details:', error)
            }
        } else {
            resetForm()
        }
    },
    { immediate: true }
)

watch(
    () => modelValue.value.asset_id,
    async (newAssetId) => {
        if (newAssetId) {
            await loadSelectedAsset()
            if (modelValue.value.seller_id) {
                await getQuantityForAccount()
            }
        } else {
            asset.value = null
        }
    }
)

watch(
    () => modelValue.value.seller_id,
    async (newSellerId) => {
        if (newSellerId && modelValue.value.asset_id) {
            await getQuantityForAccount()
        } else {
            availableRights.value = null
        }
    }
)

function resetTxForm() {
    transactionData.id = null
    transactionData.status = TransactionStatus.PENDING_PAYMENT
    transactionData.payment_reference = null
    buyerSignedFile.name =
        buyerSignedFile.upload_key =
        buyerSignedFile.url =
            null
    sellerSignedFile.name =
        sellerSignedFile.upload_key =
        sellerSignedFile.url =
            null
}

function resetForm() {
    modelValue.value = {
        id: null,
        asset_id: null,
        seller_id: null,
        buyer_id: null,
        type: SmAssetType.PRE_EMPTIVE,
        quantity: 1,
        price_per_unit: 0,
        total_price: 0,
        status: OfferStatusType.ACCEPTED,
    }
    contractFile.name = null
    contractFile.upload_key = null
    contractFile.url = null
    asset.value = null
    availableRights.value = null
    hasTransaction.value = false
    enableTransaction.value = false
    offerDetails.value = null
    resetTxForm()
}

const onContractUpload = (response: UploadFileResponse, fileName: string) => {
    contractFile.upload_key = response.key
    contractFile.url = response.temp_storage_url
    contractFile.name = fileName
}

const onContractDelete = () => {
    contractFile.name = null
    contractFile.upload_key = null
    contractFile.url = null
}

const onTxFileUpload = (
    type: string,
    response: UploadFileResponse,
    fileName: string
) => {
    if (type === 'buyer_signed') {
        buyerSignedFile.upload_key = response.key
        buyerSignedFile.url = response.temp_storage_url
        buyerSignedFile.name = fileName
    } else if (type === 'seller_signed') {
        sellerSignedFile.upload_key = response.key
        sellerSignedFile.url = response.temp_storage_url
        sellerSignedFile.name = fileName
    }
}

const onTxFileDelete = (type: string) => {
    if (type === 'buyer_signed')
        buyerSignedFile.name =
            buyerSignedFile.upload_key =
            buyerSignedFile.url =
                null
    if (type === 'seller_signed')
        sellerSignedFile.name =
            sellerSignedFile.upload_key =
            sellerSignedFile.url =
                null
}

async function getQuantityForAccount() {
    if (!modelValue.value?.seller_id || !modelValue.value?.asset_id) {
        availableRights.value = null
        return
    }

    try {
        const response = await getPreEmptiveRights({
            filter: {
                account_uid: modelValue.value.seller_id,
                asset_uid: modelValue.value.asset_id,
                ...(props.platformName
                    ? { platform_name_exact: props.platformName }
                    : {}),
            },
        })

        const items = response?.data?.data || response?.data || []

        const matchingItem = items.find(
            (item: any) => item.account_uid === modelValue.value.seller_id
        )

        availableRights.value = matchingItem ? matchingItem.quantity : 0
    } catch (error) {
        console.error('Failed to fetch pre-emptive rights quantity:', error)
        availableRights.value = 0
    }
}

async function loadSelectedAsset() {
    if (!modelValue.value.asset_id) return
    try {
        const assetResponse = await getAsset(modelValue.value.asset_id)
        asset.value = assetResponse.data.asset
    } catch (error) {
        console.error('Failed to load selected asset details:', error)
    }
}

async function loadSelectAssetOptions(search: string) {
    try {
        const assetsRequest = await getAssets({
            'filter[asset_title]': search,
        })
        const loadedAssets = assetsRequest.data.assets.map((a: Asset) => ({
            label: (a.asset_title || a.name) as string,
            value: (a.asset_uid || a.id) as string,
        }))

        if (modelValue.value.asset_id) {
            const currentSelected = assets.value.find(
                (a) => a.value === modelValue.value.asset_id
            )
            if (
                currentSelected &&
                !loadedAssets.some((a) => a.value === modelValue.value.asset_id)
            ) {
                loadedAssets.unshift(currentSelected)
            }
        }

        assets.value = loadedAssets
    } catch (error) {
        console.error('Failed to search assets:', error)
    }
}

async function loadSelectSellerAccountOptions(search: string) {
    try {
        const accountsSearchRequest = await getAccounts({
            filter: {
                search: search,
                platform_name_exact: props.platformName,
            },
        })

        const loadedAccounts = accountsSearchRequest.data.accounts.map(
            (account: Account) => ({
                label: `${account.account_title} (${account.email})`,
                value: account.account_uid as string,
            })
        )

        // Preserve the currently selected seller if it's missing from this
        // search result, so it doesn't get clobbered by the unrelated
        // account list that loads concurrently with the offer's own data.
        if (modelValue.value.seller_id) {
            const currentSelected = sellerAccounts.value.find(
                (a) => a.value === modelValue.value.seller_id
            )
            if (
                currentSelected &&
                !loadedAccounts.some(
                    (a) => a.value === modelValue.value.seller_id
                )
            ) {
                loadedAccounts.unshift(currentSelected)
            }
        }

        sellerAccounts.value = loadedAccounts
    } catch (error) {
        console.error('Failed to load seller accounts:', error)
    }
}

async function loadSelectBuyerAccountOptions(search: string) {
    try {
        const accountsSearchRequest = await getAccounts({
            filter: {
                search: search,
                platform_name_exact: props.platformName,
            },
        })

        const loadedAccounts = accountsSearchRequest.data.accounts.map(
            (account: Account) => ({
                label: `${account.account_title} (${account.email})`,
                value: account.account_uid as string,
            })
        )

        // Same rationale as loadSelectSellerAccountOptions above.
        if (modelValue.value.buyer_id) {
            const currentSelected = buyerAccounts.value.find(
                (a) => a.value === modelValue.value.buyer_id
            )
            if (
                currentSelected &&
                !loadedAccounts.some(
                    (a) => a.value === modelValue.value.buyer_id
                )
            ) {
                loadedAccounts.unshift(currentSelected)
            }
        }

        buyerAccounts.value = loadedAccounts
    } catch (error) {
        console.error('Failed to load buyer accounts:', error)
    }
}

function cancel() {
    emit('cancel')
}

function submit() {
    emit('submit', {
        ...modelValue.value,
        total_price: parseFloat(calculatedTotal.value),
        upload_key: contractFile.upload_key,
        contract_url: contractFile.url,
        contract_name: contractFile.name,

        // Transaction Data Payload
        create_transaction: enableTransaction.value || hasTransaction.value,
        transaction_status: transactionData.status,
        buyer_signed_upload_key: buyerSignedFile.upload_key,
        seller_signed_upload_key: sellerSignedFile.upload_key,
    })
}

onMounted(async () => {
    if (props.platformName) {
        try {
            const assetsRequest = await getSmAssets(props.platformName)
            const smAssets =
                assetsRequest.data?.data?.assets ||
                assetsRequest.data?.assets ||
                []

            const fetchedOptions = smAssets.map((a: any) => ({
                label: a.asset_title || a.label || a.name,
                value: a.asset_uid || a.value || a.id,
            }))

            const currentSelected = assets.value.find(
                (a) => a.value === modelValue.value.asset_id
            )

            if (
                currentSelected &&
                !fetchedOptions.some(
                    (a: any) => a.value === currentSelected.value
                )
            ) {
                assets.value = [currentSelected, ...fetchedOptions]
            } else {
                assets.value = fetchedOptions
            }
        } catch (error) {
            console.error('Failed to fetch SM assets:', error)
        }
    } else {
        await loadSelectAssetOptions('')
    }

    await loadSelectSellerAccountOptions('')
    await loadSelectBuyerAccountOptions('')
})
</script>
